import { Award } from "lucide-react";
import { calculateMatchPoints } from "@/modules/scoring/ScoringEngine";
import type { Match } from "@/modules/leagues/types/match";
import type { ScoringRule } from "@/modules/leagues/types/league";
import type { RoundBonusType } from "@/modules/leagues/types/roundBonus";
import { ROUND_BONUS_LABEL, ROUND_BONUS_MULTIPLIER } from "@/modules/leagues/types/roundBonus";
import { cn } from "@/utils/cn";

function formatKickoff(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  return {
    date: d.toLocaleDateString("pt-BR", { day: "2-digit", month: "long" }),
    time: d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }),
  };
}

interface LiveMatchCardProps {
  match: Match;
  scoringRules?: ScoringRule[];
  roundBonus?: RoundBonusType | null;
  onOpenDetail?: () => void;
}

/**
 * Card compacto pra aba "Ao vivo" (pedido do usuário, com referência
 * visual de outro app): placar PALPITADO em destaque (verde) no topo,
 * resultado REAL da partida embaixo, com a pontuação provisória que já
 * está sendo somada no ranking ao vivo (useLiveRankingBoost) — a MESMA
 * função (ScoringEngine) usada em todo lugar, nunca uma conta paralela.
 * Recalcula sozinho a cada nova busca do placar (useMatchesByRound já
 * repete a cada 30s enquanto há jogo ao vivo). Assim que a partida
 * termina, o boost do ranking para automaticamente (só soma partida com
 * status "live" — regra explícita do usuário: depois que acaba, o
 * Ranking fica parado no valor oficial até o cron validar).
 */
export function LiveMatchCard({ match, scoringRules, roundBonus, onOpenDetail }: LiveMatchCardProps) {
  const { date, time } = formatKickoff(match.kickoff);
  const hasResult = match.homeScore !== null && match.awayScore !== null;
  const hasPrediction = Boolean(match.userPrediction);

  const provisionalPoints =
    hasPrediction && hasResult && scoringRules
      ? calculateMatchPoints(
          {
            predictedHome: match.userPrediction!.homeScore,
            predictedAway: match.userPrediction!.awayScore,
            actualHome: match.homeScore as number,
            actualAway: match.awayScore as number,
          },
          scoringRules,
          roundBonus ? ROUND_BONUS_MULTIPLIER[roundBonus] : 1
        )
      : null;

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border-l-4 border-l-emerald-500 border-y border-r border-border bg-card shadow-soft",
        onOpenDetail && "cursor-pointer"
      )}
      onClick={onOpenDetail}
      role={onOpenDetail ? "button" : undefined}
      tabIndex={onOpenDetail ? 0 : undefined}
    >
      <div className="flex items-center justify-between px-4 pt-3">
        <span className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-emerald-700 dark:text-emerald-400">
          <span className="live-dot h-2 w-2 rounded-full bg-emerald-500" /> Ao vivo
        </span>
        <span className="text-xs text-muted-foreground">
          {date} às {time}
        </span>
      </div>

      {roundBonus && (
        <div className="mx-4 mt-2 flex items-center justify-center gap-1.5 rounded-lg border border-accent-500/20 bg-accent-50 py-1 dark:bg-accent-500/10">
          <Award className="h-3 w-3 text-accent-700 dark:text-accent-300" />
          <span className="text-[10px] font-bold uppercase tracking-wide text-accent-700 dark:text-accent-300">
            {ROUND_BONUS_LABEL[roundBonus]}
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex flex-1 flex-col items-center gap-1 text-center">
          {match.homeTeam.crestUrl && <img src={match.homeTeam.crestUrl} alt="" className="h-9 w-9 object-contain" />}
          <span className="line-clamp-1 text-xs font-medium">{match.homeTeam.name}</span>
        </div>

        <div className="flex flex-col items-center">
          {hasPrediction ? (
            <span className="font-display text-3xl font-bold tabular-nums text-emerald-700 dark:text-emerald-400">
              {match.userPrediction!.homeScore} <span className="text-xl text-emerald-700/60 dark:text-emerald-400/60">×</span>{" "}
              {match.userPrediction!.awayScore}
            </span>
          ) : (
            <span className="text-xs font-medium text-muted-foreground">Sem palpite</span>
          )}
        </div>

        <div className="flex flex-1 flex-col items-center gap-1 text-center">
          {match.awayTeam.crestUrl && <img src={match.awayTeam.crestUrl} alt="" className="h-9 w-9 object-contain" />}
          <span className="line-clamp-1 text-xs font-medium">{match.awayTeam.name}</span>
        </div>
      </div>

      <div className="flex items-center justify-between border-t border-border/60 px-4 py-2.5">
        <span className="text-xs font-medium text-muted-foreground">Resultado</span>
        <span className="font-display text-sm font-bold tabular-nums">
          {match.homeScore ?? 0} – {match.awayScore ?? 0}
        </span>
        {hasPrediction ? (
          provisionalPoints !== null ? (
            <span className="font-display text-sm font-bold text-emerald-700 dark:text-emerald-400">
              +{provisionalPoints} pontos
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )
        ) : (
          <span className="text-xs text-muted-foreground">Sem palpite</span>
        )}
      </div>
    </div>
  );
}
